export const SERVICES = [
  {
    slug: "bioinformatics",
    name: "Bioinformatics",
    icon: "Flask",
    tagline: "From raw reads to clinical-grade insight",
    description:
      "Validated, reproducible pipelines that turn sequencing output into decisions. We build the computational backbone that makes precision medicine operational — versioned, auditable, and fast enough for clinical timelines.",
    image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1600&q=80",
    capabilities: [
      "NGS pipeline engineering (WGS, WES, RNA-seq, methylation)",
      "Variant calling, annotation and prioritisation at scale",
      "Workflow orchestration with Nextflow and Snakemake",
      "Clinical reporting interfaces co-designed with pathologists",
      "Continuous validation against reference standards",
      "Cloud-native, cost-optimised compute architectures",
    ],
    outcomes: [
      { value: "70%", label: "faster turnaround from sample to report" },
      { value: "100%", label: "pipeline reproducibility and audit trails" },
      { value: "40%", label: "lower compute cost per genome" },
    ],
  },
  {
    slug: "genomics",
    name: "Genomics",
    icon: "Dna",
    tagline: "Population-scale genomics hospitals can trust",
    description:
      "End-to-end genomics platforms — from sample ingestion to variant interpretation — built for governance, interoperability, and clinical-grade quality control at national-programme scale.",
    image: "https://images.unsplash.com/photo-1643780668909-580822430155?auto=format&fit=crop&w=1600&q=80",
    capabilities: [
      "Germline and somatic analysis at population scale",
      "FHIR Genomics integration with EHR systems",
      "Pharmacogenomics and polygenic risk scoring",
      "Rare disease and oncology interpretation workflows",
      "Consent, governance and data-sovereignty frameworks",
      "Reference panel management and QC automation",
    ],
    outcomes: [
      { value: "End-to-end", label: "support from sample ingestion to clinical interpretation" },
      { value: "72h", label: "clinical-grade turnaround for urgent panels" },
      { value: "99.9%", label: "concordance against reference benchmarks" },
    ],
  },
  {
    slug: "clinical-solutions",
    name: "Clinical Solutions",
    icon: "Stethoscope",
    tagline: "Systems that fit the clinical workflow, not the other way round",
    description:
      "EMR optimisation, clinical decision support, and diagnostic workflow tooling designed with clinicians. We digitise care pathways without adding clicks to already stretched teams.",
    image: "https://images.pexels.com/photos/6129679/pexels-photo-6129679.jpeg?auto=compress&cs=tinysrgb&w=1600",
    capabilities: [
      "EMR/HIS implementation and optimisation",
      "Clinical decision support embedded in workflow",
      "Computerised provider order entry and e-prescribing",
      "Diagnostics and laboratory information systems",
      "Clinical documentation improvement programmes",
      "Interoperability across HL7 v2, FHIR and openEHR",
    ],
    outcomes: [
      { value: "35%", label: "reduction in documentation time per shift" },
      { value: "2x", label: "faster diagnostic turnaround visibility" },
      { value: "92%", label: "clinician adoption within first quarter" },
    ],
  },
  {
    slug: "healthcare-analytics",
    name: "Healthcare Analytics",
    icon: "ChartLineUp",
    tagline: "Predictive intelligence for clinical and operational decisions",
    description:
      "From readmission risk to theatre utilisation, we turn fragmented health data into models and dashboards that leaders and clinicians actually use — governed, explainable, and live.",
    image: "https://images.unsplash.com/photo-1644088379091-d574269d422f?auto=format&fit=crop&w=1600&q=80",
    capabilities: [
      "Predictive models for risk, demand and outcomes",
      "Real-time operational command-centre dashboards",
      "Population health stratification and registries",
      "Clinical quality and safety analytics",
      "Revenue-cycle and cost-of-care intelligence",
      "Governed self-service analytics platforms",
    ],
    outcomes: [
      { value: "Live", label: "dashboards designed for real clinical operations" },
      { value: "12hrs", label: "saved weekly per operations team" },
      { value: "50+", label: "live dashboards across partner networks" },
    ],
  },
  {
    slug: "patient-care",
    name: "Patient Care",
    icon: "Heartbeat",
    tagline: "Continuous care beyond the hospital walls",
    description:
      "Remote monitoring, virtual care, and care-coordination platforms that keep patients connected to their care teams before, during, and long after a hospital episode.",
    image: "https://images.pexels.com/photos/6129679/pexels-photo-6129679.jpeg?auto=compress&cs=tinysrgb&w=1600",
    capabilities: [
      "Remote patient monitoring and device integration",
      "Virtual consultation and telehealth platforms",
      "Care pathway and discharge coordination tooling",
      "Chronic disease management programmes",
      "Medication adherence and follow-up automation",
      "Escalation triage powered by clinical rules",
    ],
    outcomes: [
      { value: "24/7", label: "continuity of care beyond discharge" },
      { value: "30%", label: "fewer avoidable emergency visits" },
      { value: "85%", label: "patient adherence to care plans" },
    ],
  },
  {
    slug: "digital-transformation",
    name: "Digital Transformation",
    icon: "Devices",
    tagline: "Workflow redesign first, technology second",
    description:
      "Enterprise digitisation programmes that start with clinical and operational workflows — interoperability, cloud migration, and change management that compounds value year after year.",
    image: "https://images.unsplash.com/photo-1643780668909-580822430155?auto=format&fit=crop&w=1600&q=80",
    capabilities: [
      "Digital maturity assessment and roadmap design",
      "Legacy modernisation and cloud migration",
      "Standards-based interoperability (FHIR, openEHR, DICOM)",
      "Process re-engineering for admissions to discharge",
      "Change management and clinician enablement",
      "Data platform and integration architecture",
    ],
    outcomes: [
      { value: "3x", label: "faster delivery of subsequent digital projects" },
      { value: "60%", label: "reduction in integration maintenance cost" },
      { value: "100+", label: "systems connected across client estates" },
    ],
  },
  {
    slug: "patient-experience",
    name: "Patient Experience",
    icon: "UserFocus",
    tagline: "Every touchpoint, measured and redesigned",
    description:
      "Journey-led design of the moments that define how patients feel about care — booking, arrival, communication, billing, and discharge — instrumented with experience data end-to-end.",
    image: "/assets/patient-experience.jpeg",
    capabilities: [
      "Patient journey mapping and experience research",
      "Omnichannel scheduling and communication",
      "Digital front door: portals, apps and wayfinding",
      "Transparent billing and payment experiences",
      "Real-time feedback and experience measurement",
      "Accessibility and inclusive design compliance",
    ],
    outcomes: [
      { value: "+22", label: "average NPS uplift within two quarters" },
      { value: "45%", label: "of appointments self-scheduled by patients" },
      { value: "90%", label: "first-contact resolution on enquiries" },
    ],
  },
  {
    slug: "personalised-healthcare",
    name: "Personalised Healthcare",
    icon: "Brain",
    tagline: "Care plans as unique as the genome behind them",
    description:
      "The convergence of genomics, analytics, and patient data into individualised prevention and treatment — matching the right intervention to the right patient at the right moment.",
    image: "/assets/patient-experience.jpeg",
    capabilities: [
      "Genomically-informed treatment selection",
      "Individualised risk and prevention programmes",
      "Therapy-response prediction models",
      "Longitudinal patient data unification",
      "Personalised engagement and adherence design",
      "Clinical trial matching and recruitment analytics",
    ],
    outcomes: [
      { value: "28%", label: "improvement in therapy response matching" },
      { value: "5x", label: "faster trial-to-patient matching" },
      { value: "1:1", label: "care plans tailored to each patient profile" },
    ],
  },
  {
    slug: "cost-effective-engagement",
    name: "Cost-Effective Engagement",
    icon: "HandCoins",
    tagline: "Bringing down the cost of patient engagement and experience",
    description:
      "Great patient experience doesn't have to be expensive. We design digital-first, automation-led engagement that brings down the overall cost of patient engagement and experience — fewer phone calls, fewer manual follow-ups, more self-service — without making care feel colder.",
    image: "/assets/patient-experience.jpeg",
    capabilities: [
      "Automated reminders, follow-ups and recalls across WhatsApp, SMS and email",
      "Self-service scheduling, rescheduling and digital check-in",
      "AI-assisted triage and FAQ handling to cut call-centre load",
      "Digital-first patient journeys that reduce front-desk workload",
      "Experience measurement to target spend where it matters",
      "Cost-to-serve analysis across every engagement channel",
    ],
    outcomes: [
      { value: "Lower", label: "cost-to-serve across engagement channels" },
      { value: "Self-service", label: "first — patients help themselves, staff handle exceptions" },
      { value: "Measurable", label: "savings tracked against experience scores, not instead of them" },
    ],
  },
];

export const PLATFORMS = [
  {
    slug: "geneiux",
    name: "Anxya Health",
    icon: "Atom",
    external: true,
    tagline: "Healthcare & Life Sciences on Anxya AI OS & PaaS",
    bandTitle: "the AI OS for healthcare & life sciences",
    bandText: "ACE — an Autonomous, Compliant, self-Evolving, patient-centric global healthcare ecosystem for personalised & precision medicine and care. Owned by you, built for the world. 369 agents, 69 multi-agent workflows, and an app builder that goes from idea to compliant solution in clicks.",
    description:
      "Anxya Health is the first vertical implementation of Anxya AI OS & PaaS — ACE: an Autonomous, Compliant, self-Evolving, patient-centric global healthcare ecosystem for personalised and precision medicine. Built on the 6 Ds — Default Compliant, Decentralised, Democratic, Distributed, Data-sovereign, Decommodified — and the 3 Ps: Personalised, Precision, Patient-centric. Outputs aligned with HIPAA, GxP, 21 CFR Part 11, GDPR and the EU AI Act.",
    website: "https://www.anxya.health",
    image: "/assets/geneiux-product.png",
    video: "/assets/geneiux-demo.mp4",
    capabilities: [
      "ACE Builder — web apps, mobile apps, business plans & consulting from a prompt",
      "369 domain-specialised autonomous agents, evidence-cited and audit-ready",
      "69 multi-agent workflows with human-in-the-loop compliance gates",
      "Compliance Engine — HIPAA, GxP, 21 CFR Part 11, EU AI Act gap plans",
      "IT risk, AI maturity, competitive intelligence & SWOT on demand",
      "Live platform — start building at anxya.health",
    ],
    tools: [
      { name: "ACE Builder", desc: "Idea to working, clickable application from a ready-made prompt" },
      { name: "Agents", desc: "369+ specialist autonomous agents — evidence-cited, governed, audit-ready" },
      { name: "APIs", desc: "Agentic infrastructure and SDKs exposed through REST APIs and webhooks" },
      { name: "Workflows", desc: "69 orchestrated multi-agent pipelines with compliance gates" },
      { name: "Connectors", desc: "Universal enterprise integration across 12+ protocols" },
      { name: "Data", desc: "Approved, sovereign data foundation with verified knowledge" },
      { name: "Search Anxya", desc: "Clean, clear, ad-free health and life-science search" },
      { name: "Ask Anxya", desc: "Straight answers — no ads, no promos" },
      { name: "Signals & Insights", desc: "Live intelligence and research signals for healthcare teams" },
      { name: "Compliance Engine", desc: "Score your org against HIPAA, GxP, 21 CFR Part 11, EU AI Act" },
      { name: "IT Risk Assessment", desc: "IT & cyber risk score with a mitigation roadmap" },
      { name: "AI Maturity Check", desc: "Benchmark AI readiness from your own documents" },
      { name: "Competitive Intelligence", desc: "Market-landscape briefs on demand" },
      { name: "Product Safety", desc: "Molecular scan — ingredients, risks, adverse events" },
      { name: "SWOT Report", desc: "Structured strategic analysis in minutes" },
      { name: "Healthcare University", desc: "Learn, teach & earn — 70% on every course" },
      { name: "Health Wiki", desc: "10,000+ ad-free health & life-science topics" },
      { name: "Health & LS News", desc: "Ad-free regulatory & compliance updates" },
      { name: "Professionals", desc: "Network, post & connect with peers" },
      { name: "Smart Drive", desc: "Encrypted, AI-organized cloud storage with sharing and version history" },
    ],
    outcomes: [
      { value: "369", label: "specialist agents, evidence-cited and audit-ready" },
      { value: "69", label: "multi-agent workflows with compliance gates" },
      { value: "Live", label: "in production today at anxya.health" },
    ],
  },
];

export const PRODUCTS = [
  {
    slug: "eaglei",
    name: "Eaglei",
    icon: "Eye",
    tagline: "AI-native LIMS — the lean backbone of the modern regulated lab",
    bandTitle: "the AI-native LIMS that costs like one",
    bandText: "Eaglei is what happens when a LIMS is built on advanced AI from day one — lean, lightning-fast, compliant and secure by design, at a fraction of what the elephant vendors charge.",
    description:
      "Eaglei is an AI-native LIMS built the way we build everything — lean, cost-effective, compliant and secure, with none of the bloat of legacy elephant vendors. It unifies sample management, chain of custody, compliance, inventory, instruments and AI analytics into one command center, built for 21 CFR Part 11, ISO 17025 and GxP. Managed cloud and tablet, ready for regulated labs out of the box.",
    website: "https://www.eaglei.digital",
    image: "/assets/eaglei-product.png",
    video: "/assets/eaglei-demo.mp4",
    capabilities: [
      "Sample management and full chain of custody",
      "Immutable, hash-chained audit trail with e-signatures",
      "21 CFR Part 11, ISO 17025 and GxP compliance built in",
      "Instrument and inventory management in one console",
      "AI analytics across lab operations",
      "Role-based access with managed cloud and tablet apps",
    ],
    outcomes: [
      { value: "100%", label: "sample traceability across the lab" },
      { value: "6", label: "integrated modules in one command center" },
      { value: "0", label: "editable audit records — compliance by design" },
    ],
  },
  {
    slug: "patexcite",
    name: "PatExcite",
    icon: "Sparkle",
    tagline: "Digital twin, personalised precision healthcare",
    bandTitle: "your digital twin for precision health",
    bandText: "PatExcite builds a living digital twin of every patient — reports update in real-time, and AI turns genetics, labs and vitals into personalised precision insights. Patients know their body, act early, and even earn on their data.",
    description:
      "PatExcite is Anxya Tech's digital-twin product for personalised precision healthcare. Patients update their reports and health data in real-time, and the platform's AI maintains a living digital twin — delivering personalised precision insights, early signals, and guidance. Built for patients, hospitals, clinicians, and life-science teams alike.",
    website: "https://patexcite.com",
    image: "/assets/patexcite-product.png",
    video: "/assets/patexcite-demo.mp4",
    capabilities: [
      "Living digital twin for every patient",
      "Real-time report updates and health tracking",
      "Personalised precision insights from genetics, labs and vitals",
      "Early signals and AI guidance for patients and clinicians",
      "Consent-based data sharing with patient rewards",
      "Live product — explore it at patexcite.com",
    ],
    outcomes: [
      { value: "Real-time", label: "digital twin updated with every report and reading" },
      { value: "Personalised", label: "precision insights unique to each patient's biology" },
      { value: "Live", label: "in production today at patexcite.com" },
    ],
  },
  {
    slug: "clinixify",
    name: "Clinixify",
    icon: "Pulse",
    tagline: "Clinical trials analytics, intelligence and insights",
    bandTitle: "clinical trial intelligence, in plain English",
    bandText: "Search clinical trials by drug or biomarker and get AI answers anyone can understand — synced live with ClinicalTrials.gov. Built for hospitals, clinicians, patients, and pharma teams.",
    description:
      "Clinixify (ClinX) is Anxya Tech's flagship product — a clinical trial intelligence platform where anyone can search trials by drug or biomarker and get answers in plain English from AI. Synced live with ClinicalTrials.gov, it serves hospitals, clinicians, patients, and pharma executives alike.",
    website: "https://www.clinixify.com",
    image: "/assets/clinixify-product.png",
    video: "/assets/clinixify-demo.mp4",
    capabilities: [
      "Search clinical trials by drug, biomarker, or condition",
      "AI answers about any trial, in plain English",
      "Live sync from ClinicalTrials.gov",
      "Phase, sites, and objective at a glance for every result",
      "Saved trials and live sync for research teams",
      "Built for hospitals, clinicians, patients, and pharma",
    ],
    outcomes: [
      { value: "Real-time", label: "visibility into every trial, site, and cohort" },
      { value: "For everyone", label: "hospitals, clinicians, patients, and pharma in one view" },
      { value: "Live", label: "in production today at clinixify.com" },
    ],
  },
  {
    slug: "mastercxm",
    name: "MasterCXM",
    soon: true,
    icon: "Users",
    tagline: "AI-native customer & patient experience management",
    bandTitle: "experience management that manages itself",
    bandText: "MasterCXM is our AI-native experience management platform — every patient and customer journey measured, understood, and improved in real-time, at a fraction of what legacy CXM suites charge.",
    description:
      "MasterCXM is Anxya Tech's AI-native customer and patient experience management platform. It unifies every journey — from first enquiry to discharge and beyond — into one intelligent console that measures, predicts, and improves experience in real-time. Lean, fast, compliant and secure by design, at a fraction of what legacy CXM suites charge.",
    website: "https://www.mastercxm.digital",
    image: "/assets/patient-experience.jpeg",
    capabilities: [
      "Unified patient and customer journey management",
      "Real-time experience measurement and signals",
      "AI-driven journey orchestration and next-best actions",
      "Feedback, surveys and sentiment in one console",
      "Complaint, query and escalation automation",
      "Launching soon at mastercxm.digital",
    ],
    outcomes: [
      { value: "Real-time", label: "experience signals across every journey" },
      { value: "AI-native", label: "journeys that improve themselves" },
      { value: "Soon", label: "launching at mastercxm.digital" },
    ],
  },
  {
    slug: "mykind",
    name: "My Kind",
    icon: "Heart",
    external: true,
    tagline: "Your kind of people — social, health, learning & giving",
    bandTitle: "my universe, connected",
    bandText: "My Kind is a kindness-first community platform — connect with your kind of people, practice yoga & mindfulness, boost wellbeing and longevity, learn, earn, and give back through philanthropy, as one conscious family.",
    description:
      "My Kind is a kindness-first community platform: social, health, education, travel, food and events — personalised. Your profile, communities, health regimen, habits, books, documents and life story, all owned by you, with a personal assistant for everything. Connect, post, share, learn, earn — and give back.",
    website: "https://mykind.app",
    image: "/assets/mykind-product.png",
    capabilities: [
      "Social profile, communities and network — your kind of people",
      "Health regimen, habits, wellbeing and longevity tracking",
      "VishwaGuru learning — books, interpretation, courses",
      "Community travel, events and food experiences",
      "Ask or search — and earn while you do",
      "Live today at mykind.app",
    ],
    outcomes: [
      { value: "One family", label: "connect, have fun, learn, earn, be well" },
      { value: "Owned by you", label: "your data, your story, your universe" },
      { value: "Live", label: "in production today at mykind.app" },
    ],
  },
];
